import React, { Fragment } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { LenderPerformanceFunnelViewModel } from '@trnsact/trnsact-shared-types';
import { FUNNEL_CONVERSIONS, FUNNEL_MAX_BAR_HEIGHT, FUNNEL_STAGES } from '../../../../constants';
import { formatPercentValue, getFunnelBarHeight, getFunnelPercentOfSubmitted } from '../../../../lib';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { USE_PDF_SELECTOR } from 'hooks/usePdf';

interface Props {
  funnel: LenderPerformanceFunnelViewModel | null;
  loading?: boolean;
}

export const PipelineFunnelCard = ({ funnel, loading }: Props) => {
  const classes = useStyles();

  const submitted = funnel?.submitted ?? 0;

  if (loading) {
    return (
      <CardContainer title="Pipeline Funnel">
        <Skeleton width={280} height={20} sx={{ mb: 2 }} />

        <Stack direction="row" alignItems="flex-end" gap={1} className={classes.row}>
          {FUNNEL_STAGES.map((stage, index) => (
            <Skeleton
              key={stage.key}
              variant="rounded"
              className={classes.stage}
              height={FUNNEL_MAX_BAR_HEIGHT * (1 - index * 0.15)}
            />
          ))}
        </Stack>
      </CardContainer>
    );
  }

  return (
    <CardContainer title="Pipeline Funnel">
      {funnel && (
        <Box className={classes.summary}>
          <Typography variant="body2" color="text.secondary">
            <Typography variant="subtitle2" component="span" sx={{ fontWeight: 500, mr: 0.5, color: 'black' }}>
              Look-to-Book: {formatPercentValue(funnel.lookToBook)}
            </Typography>{' '}
            {funnel.funded} funded out of {submitted} submitted
          </Typography>
        </Box>
      )}

      <Stack direction="row" alignItems="flex-end" gap={1} className={classes.row}>
        {FUNNEL_STAGES.map((stage, index) => {
          const value = funnel?.[stage.key] ?? 0;
          const conversion = FUNNEL_CONVERSIONS[index];

          return (
            <Fragment key={stage.key}>
              <Stack className={classes.stage} alignItems="center" spacing={1}>
                <Box
                  className={classes.bar}
                  style={{ height: getFunnelBarHeight(value, submitted), backgroundColor: stage.color }}
                >
                  <Box className={classes.valuePill}>
                    <Typography variant="h5" fontWeight={600}>
                      {value.toLocaleString()}
                    </Typography>
                  </Box>

                  <Typography variant="body2">
                    {stage.key === 'submitted' ? (
                      '100%'
                    ) : (
                      <>
                        {getFunnelPercentOfSubmitted(value, submitted)}
                        <br />
                        of submitted
                      </>
                    )}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" component="span">
                  {stage.label}
                </Typography>
              </Stack>

              {conversion && (
                <Stack alignItems="center" justifyContent="center" className={classes.conversion}>
                  <ArrowRightAltIcon />
                  <Typography variant="caption" noWrap>
                    {formatPercentValue(funnel?.[conversion.key] ?? 0)} {conversion.label}
                  </Typography>
                </Stack>
              )}
            </Fragment>
          );
        })}
      </Stack>
    </CardContainer>
  );
};

const useStyles = makeStyles(theme => ({
  summary: {
    width: 'fit-content',
    alignSelf: 'flex-start',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
    [`${USE_PDF_SELECTOR} &`]: {
      padding: theme.spacing(0.5, 1),
      marginBottom: theme.spacing(1),
    },
  },
  row: {
    marginTop: theme.spacing(1),
  },
  stage: {
    flex: 1,
  },
  bar: {
    width: '100%',
    display: 'flex',
    textAlign: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(2),
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    [`${USE_PDF_SELECTOR} &`]: {
      padding: theme.spacing(0.75),
      maxHeight: '90px !important',
    },
  },
  valuePill: {
    alignSelf: 'center',
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    [`${USE_PDF_SELECTOR} &`]: {
      padding: '1px 4px',
      marginBottom: 0,
      '& .MuiTypography-root': { fontSize: '13px !important', lineHeight: 1.3 },
    },
  },
  conversion: {
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    [`${USE_PDF_SELECTOR} &`]: {
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
    },
  },
}));
